import * as _ from 'lodash-es';
import path from 'path';
import api from '../../api';
import models from '../../../models';
import { createCallToolResult, createTextContent } from '../output-parser';
import file from '../../../utils/file';
import output from '../../../utils/output-writer';
import type { WrappedRegisterToolFuncType } from '../tool-definition';

const runPlaybookBatchInputSchema = models.tool.runPlaybookBatchInput;

const allResultsHaveSameFailure = (results: Record<string, unknown>[]) => {
  const failureResults = results.map((result) => result.failureResults);
  if (_.some(failureResults, _.isEmpty)) return false;

  return _.uniqWith(failureResults, _.isEqual).length === 1;
};

export const setup = (register: WrappedRegisterToolFuncType) => {
  register(
    'run-playbook',
    {
      description: 'Run the playbooks for one or more message links',
      inputSchema: runPlaybookBatchInputSchema,
    },
    async (input) => {
      const {
        inputList,
        bookPath,
        bookName: explicitBookName,
        outputPath,
        verbose = false,
      } = input;

      if (bookPath && explicitBookName) {
        throw new Error(
          'Cannot specify both bookPath and bookName. Use bookPath to provide a local YAML file, or bookName to select an existing book.'
        );
      }

      const bookName =
        explicitBookName ?? (bookPath && path.basename(bookPath, '.yaml'));

      output.info(
        `Running playbook ${bookName ?? ''} with message links ${JSON.stringify(inputList)}`
      );

      const bookYamlStr = bookPath && (await file.readFile(bookPath));

      const data = await api.runPlaybooks({
        inputList,
        bookYamlStr,
        bookName,
        verbose,
      });

      if ('error' in data) {
        throw new Error(JSON.stringify(data));
      }

      if (Array.isArray(data) && allResultsHaveSameFailure(data)) {
        throw new Error(JSON.stringify(data[0]));
      }

      const resultJson = JSON.stringify(data, null, 2);

      if (outputPath) {
        await file.writeFile(outputPath, resultJson);
        return createCallToolResult({
          contentBlocks: [createTextContent(`Output written to ${outputPath}`)],
        });
      }

      return createCallToolResult({
        contentBlocks: [createTextContent(resultJson)],
      });
    }
  );
};

export default { setup };
